/**
 * MethodInputFields — the SINGLE SOURCE for every print method's pricing inputs.
 *
 * Rendered by both the primary print-group card (ArtworkGroupCard) and the
 * comparison panel (MethodComparisonPanel), so a method's inputs are defined
 * in exactly one place. `context` controls the small layout differences:
 *   - "primary": the size-tier selector is rendered by the card's shared grid, so it is skipped here.
 *   - "comparison": size-tier is rendered here (there is no shared grid).
 * When a live `breakdown` is supplied the method's cost breakdown is shown too.
 */
import { useEffect } from "react";
import { AlertCircle, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SIZE_TIERS, BLOCKERS, SHEET_SIZES, formatCurrency } from "./constants";
import { useTspConfig } from "./useTspConfig";

export default function MethodInputFields({
  method,
  group,
  onUpdate,
  onUpdateFields,
  index = 0,
  breakdown = null,
  totalUnits = 0,
  context = "primary",
}) {
  const isComparison = context === "comparison";
  const updateFields = onUpdateFields || ((fields) => Object.entries(fields).forEach(([k, v]) => onUpdate(k, v)));
  const tspConfig = useTspConfig(method === "Traditional Screen Print");

  // Traditional Screen Print defaults (Film + Screen selected, 1 colour)
  useEffect(() => {
    if (method === "Traditional Screen Print") {
      const patch = {};
      if (group.tsp_selected_charges === undefined || group.tsp_selected_charges === null) {
        patch.tsp_selected_charges = ["Film", "Screen"];
      }
      if (!group.tsp_colours) patch.tsp_colours = 1;
      if (Object.keys(patch).length > 0) updateFields(patch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  const SizeTier = () => (
    <div className="space-y-1">
      <Label className="text-xs">Size Tier</Label>
      <Select value={group.size_tier || ""} onValueChange={(v) => onUpdate("size_tier", v)}>
        <SelectTrigger data-testid={`artwork-size-${index}`}><SelectValue placeholder="Select size..." /></SelectTrigger>
        <SelectContent>
          {SIZE_TIERS.map((tier) => <SelectItem key={tier} value={tier}>{tier}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  // ==================== SCREEN PRINT PRO ====================
  if (method === "Screen Print Pro") {
    return (
      <div className="pt-3 border-t space-y-4">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block">Screen Print Pro (Velflex 2026)</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <Label className="text-sm font-medium">Garments per Sheet</Label>
            <Input type="number" min="1" max="50" value={group.prints_per_sheet || 1}
              onChange={(e) => onUpdate("prints_per_sheet", Math.max(1, parseInt(e.target.value) || 1))}
              className="h-9 font-mono" data-testid={`spp-garments-per-sheet-${index}`} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Colours</Label>
            <Input type="number" min="1" max="6" value={group.screens || 1}
              onChange={(e) => onUpdate("screens", Math.min(6, Math.max(1, parseInt(e.target.value) || 1)))}
              className="h-9 font-mono" data-testid={`spp-colour-count-${index}`} />
            <p className="text-[11px] text-muted-foreground">1-6 colours</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Sheet Size</Label>
            <Select value={group.sheet_size || "Standard"} onValueChange={(v) => onUpdate("sheet_size", v)}>
              <SelectTrigger className="h-9" data-testid={`spp-sheet-size-${index}`}><SelectValue placeholder="Sheet size" /></SelectTrigger>
              <SelectContent>{SHEET_SIZES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">A3 gang sheet tier</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch id={`spp-light-dark-${index}`} checked={group.light_on_dark || false}
            onCheckedChange={(c) => onUpdate("light_on_dark", c)} data-testid={`spp-light-dark-${index}`} />
          <Label htmlFor={`spp-light-dark-${index}`} className="text-sm cursor-pointer">Light on Dark</Label>
          <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-muted-foreground cursor-help" /></TooltipTrigger>
            <TooltipContent><p className="text-xs">Adds +1 colour for white underbase</p></TooltipContent></Tooltip></TooltipProvider>
        </div>
        {breakdown && !breakdown.error && (
          <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Sheets{breakdown.sheet_tier ? ` (${breakdown.sheet_tier})` : ""}:</span><span className="font-mono">{breakdown.sheets_required}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Sheet Cost:</span><span className="font-mono">{formatCurrency(breakdown.print_cost || 0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Setup ({breakdown.effective_colours} × ${breakdown.setup_per_colour || 40}):</span><span className="font-mono">{formatCurrency(breakdown.setup_cost || 0)}</span></div>
            <div className="flex justify-between pt-1 border-t border-slate-300 font-bold"><span className="text-slate-700">Total:</span><span className="font-mono">{formatCurrency(breakdown.cost || 0)}</span></div>
          </div>
        )}
      </div>
    );
  }

  // ==================== ULTRACOLOUR REFLECTIVE ====================
  if (method === "UltraColour Reflective") {
    return (
      <div className="pt-3 border-t space-y-4">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block">UltraColour Reflective</Label>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Garments per Sheet</Label>
            <Input type="number" min="1" max="50" value={group.prints_per_sheet || 1}
              onChange={(e) => onUpdate("prints_per_sheet", Math.max(1, parseInt(e.target.value) || 1))}
              className="h-9 font-mono" data-testid={`ucr-garments-per-sheet-${index}`} />
            <p className="text-[11px] text-muted-foreground">Min 20 units. $40 setup per colour.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Colours</Label>
            <Input type="number" min="1" max="3" value={group.screens || 1}
              onChange={(e) => onUpdate("screens", Math.min(3, Math.max(1, parseInt(e.target.value) || 1)))}
              className="h-9 font-mono" data-testid={`ucr-colour-count-${index}`} />
            <p className="text-[11px] text-muted-foreground">Max 3 colours</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <div className="flex items-center gap-2">
            <Switch id={`ucr-bw-${index}`} checked={group.print_colour === "White" || group.print_colour === "Black"}
              onCheckedChange={(c) => onUpdate("print_colour", c ? "Black" : "Other")} data-testid={`ucr-black-white-${index}`} />
            <Label htmlFor={`ucr-bw-${index}`} className="text-sm cursor-pointer">Black/White Only</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id={`ucr-lod-${index}`} checked={group.light_on_dark || false}
              onCheckedChange={(c) => onUpdate("light_on_dark", c)} data-testid={`ucr-light-dark-${index}`} />
            <Label htmlFor={`ucr-lod-${index}`} className="text-sm cursor-pointer">Light on Dark</Label>
          </div>
        </div>
        {breakdown && !breakdown.error && (
          <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Sheets Required{breakdown.sheet_tier ? ` (${breakdown.sheet_tier})` : ""}:</span><span className="font-mono">{breakdown.sheets_required}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Sheet Cost:</span><span className="font-mono">{formatCurrency(breakdown.print_cost || 0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Setup ({breakdown.effective_colours} × ${breakdown.setup_per_colour || 40}):</span><span className="font-mono">{formatCurrency(breakdown.setup_cost || 0)}</span></div>
            <div className="flex justify-between pt-1 border-t border-slate-300 font-bold"><span className="text-slate-700">Total:</span><span className="font-mono">{formatCurrency(breakdown.cost || 0)}</span></div>
          </div>
        )}
      </div>
    );
  }

  // ==================== TRADITIONAL SCREEN PRINT ====================
  if (method === "Traditional Screen Print") {
    const colourOptions = (tspConfig.colours && tspConfig.colours.length > 0) ? tspConfig.colours : [1, 2, 3, 4, 5, 6];
    const selectedCharges = group.tsp_selected_charges ?? ["Film", "Screen"];
    const tspColours = group.tsp_colours || 1;
    const units = breakdown?.total_placements || totalUnits || 0;
    const toggleCharge = (name, on) => {
      const next = on ? [...new Set([...selectedCharges, name])] : selectedCharges.filter((c) => c !== name);
      onUpdate("tsp_selected_charges", next);
    };
    const setRepeatJob = (on) => {
      const waivable = ["Film", "Screen"];
      const next = on ? selectedCharges.filter((c) => !waivable.includes(c)) : [...new Set([...selectedCharges, ...waivable])];
      updateFields({ tsp_repeat_job: on, tsp_selected_charges: next });
    };
    return (
      <div className="pt-3 border-t space-y-4">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block">Traditional Screen Print</Label>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Colours</Label>
            <Select value={String(tspColours)} onValueChange={(v) => onUpdate("tsp_colours", parseInt(v))}>
              <SelectTrigger className="h-9" data-testid={`tsp-colour-count-${index}`}><SelectValue /></SelectTrigger>
              <SelectContent>{colourOptions.map((c) => <SelectItem key={c} value={String(c)}>{c} Colour{c !== 1 ? "s" : ""}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">From uploaded matrix</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Repeat Job</Label>
            <div className="flex items-center gap-2 h-9">
              <Switch id={`tsp-repeat-${index}`} checked={group.tsp_repeat_job || false} onCheckedChange={setRepeatJob} data-testid={`tsp-repeat-job-${index}`} />
              <Label htmlFor={`tsp-repeat-${index}`} className="text-xs cursor-pointer text-muted-foreground">Waive Film &amp; Screen charges</Label>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">One-Off Job Charges</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {(tspConfig.charges || []).map((charge) => {
              const name = charge.ChargeName;
              const ctype = String(charge.ChargeType || "flat").toLowerCase().replace(/_/g, "-").replace(/\s/g, "-");
              const isPerColour = ctype.includes("colour") || ctype.includes("color");
              const isPerUnit = ctype.includes("unit") || ctype.includes("piece");
              const isPerMatch = ctype.includes("match");
              const matchQty = (group.tsp_charge_quantities || {})[name] ?? 1;
              let amount;
              if (isPerColour) amount = (charge.Amount || 0) * tspColours;
              else if (isPerUnit) amount = (charge.Amount || 0) * units;
              else if (isPerMatch) amount = (charge.Amount || 0) * matchQty;
              else amount = charge.Amount || 0;
              const checked = selectedCharges.includes(name);
              const unitLabel = isPerColour ? "/colour" : isPerUnit ? "/unit" : isPerMatch ? "/match" : "";
              const slug = name.replace(/\s+/g, "-").toLowerCase();
              return (
                <div key={name} className={`flex items-center justify-between gap-2 rounded border px-2 py-1.5 ${checked ? "bg-blue-50 border-blue-200" : "bg-white"}`} data-testid={`tsp-charge-${index}-${slug}`}>
                  <label className="flex items-center gap-2 min-w-0 cursor-pointer flex-1">
                    <Checkbox checked={checked} onCheckedChange={(v) => toggleCharge(name, !!v)} />
                    <span className="text-sm truncate">{name}</span>
                    {unitLabel && <span className="text-[10px] text-muted-foreground">{unitLabel}</span>}
                  </label>
                  {isPerMatch && checked && (
                    <Input type="number" min="1" value={matchQty}
                      onChange={(e) => onUpdate("tsp_charge_quantities", { ...(group.tsp_charge_quantities || {}), [name]: Math.max(1, parseInt(e.target.value) || 1) })}
                      className="h-7 w-14 font-mono text-xs px-1.5" data-testid={`tsp-charge-qty-${index}-${slug}`} />
                  )}
                  <span className="font-mono text-xs text-slate-500">{formatCurrency(amount)}</span>
                </div>
              );
            })}
          </div>
          {(tspConfig.charges || []).length === 0 && (
            <p className="text-xs text-muted-foreground">No one-off charges configured. Add them in Settings → Pricing Tables.</p>
          )}
        </div>
        {breakdown && !breakdown.error && (
          <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Print ({units} × {formatCurrency(breakdown.price_per_unit || 0)}):</span>
              <span className="font-mono">{formatCurrency(breakdown.print_cost || 0)}</span>
            </div>
            {(breakdown.charge_lines || []).map((c, i) => (
              <div key={i} className="flex justify-between" data-testid={`tsp-breakdown-charge-${index}-${i}`}>
                <span className="text-slate-500">{c.name}{c.qty_label ? ` (${c.qty_label})` : ""}:</span>
                <span className="font-mono">{formatCurrency(c.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-1 border-t border-slate-300 font-bold"><span className="text-slate-700">Total:</span><span className="font-mono">{formatCurrency(breakdown.cost || 0)}</span></div>
          </div>
        )}
        {breakdown?.error && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" /><p className="text-sm text-amber-700">{breakdown.error}</p>
          </div>
        )}
      </div>
    );
  }

  // ==================== ULTRACOLOUR PRO ====================
  if (method === "UltraColour Pro") {
    return (
      <div className="pt-3 border-t">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 block">UltraColour Pro Settings</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {isComparison && <SizeTier />}
          <div className="space-y-1">
            <Label className="text-xs">Blocker Type</Label>
            <Select value={group.blocker || ""} onValueChange={(v) => onUpdate("blocker", v)}>
              <SelectTrigger data-testid={`artwork-uc-pro-blocker-${index}`}><SelectValue placeholder="Select blocker..." /></SelectTrigger>
              <SelectContent>{BLOCKERS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  // ==================== ULTRACOLOUR MAX / DTG (size tier only) ====================
  if (method === "UltraColour Max" || method === "DTG") {
    if (!isComparison) return null; // primary card renders size tier in its shared grid
    return (
      <div className="pt-3 border-t">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 block">{method} Settings</Label>
        <div className="grid grid-cols-2 gap-3"><SizeTier /></div>
      </div>
    );
  }

  // ==================== EMBROIDERY ====================
  if (method === "Embroidery") {
    return (
      <div className="pt-3 border-t">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 block">Embroidery Settings</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Stitch Count</Label>
            <Input type="number" min="9999" step="1000" placeholder="9999" value={group.stitch_count || 9999}
              onChange={(e) => onUpdate("stitch_count", parseInt(e.target.value) || 9999)}
              data-testid={`artwork-stitch-count-${index}`} className="font-mono" />
          </div>
          {!isComparison && (
            <div className="space-y-1">
              <Label className="text-xs">Est. Time per Unit (mins)</Label>
              <Input type="number" min="0" placeholder="Minutes per garment" value={group.estimated_time_mins || ""}
                onChange={(e) => onUpdate("estimated_time_mins", parseInt(e.target.value) || 0)}
                data-testid={`artwork-emb-time-${index}`} className="font-mono" />
              <p className="text-[10px] text-muted-foreground">Time to embroider each garment</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== MANUAL OVERRIDE ====================
  if (method === "Manual Override") {
    if (isComparison) {
      return (
        <div className="pt-3 border-t bg-amber-50 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
          <span className="text-xs text-amber-700">Manual Override uses custom pricing and cannot be auto-calculated for a comparison.</span>
        </div>
      );
    }
    return (
      <div className="pt-3 border-t">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 block">Manual Override - Custom Pricing</Label>
        <p className="text-xs text-muted-foreground mb-3">Enter custom pricing. All fields are optional - fill in what you need.</p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="space-y-1">
            <Label className="text-xs">Sell Price (ex GST)</Label>
            <Input type="number" step="0.01" min="0" placeholder="Enter sell price..." value={group.override_sell_ex_gst ?? ""}
              onChange={(e) => onUpdate("override_sell_ex_gst", e.target.value ? parseFloat(e.target.value) : null)}
              data-testid={`artwork-override-sell-${index}`} className="font-mono" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Internal Cost (ex GST)</Label>
            <Input type="number" step="0.01" min="0" placeholder="Enter cost..." value={group.override_cost_ex_gst ?? ""}
              onChange={(e) => onUpdate("override_cost_ex_gst", e.target.value ? parseFloat(e.target.value) : null)}
              data-testid={`artwork-override-cost-${index}`} className="font-mono" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Label (optional)</Label>
            <Input placeholder="e.g., Special embroidery..." value={group.override_label || ""}
              onChange={(e) => onUpdate("override_label", e.target.value)} data-testid={`artwork-override-label-${index}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Est. Time (mins) - optional</Label>
            <Input type="number" min="0" placeholder="Time estimate..." value={group.override_time_mins ?? ""}
              onChange={(e) => onUpdate("override_time_mins", e.target.value ? parseFloat(e.target.value) : null)}
              data-testid={`artwork-override-time-${index}`} className="font-mono" />
          </div>
        </div>
        {group.override_sell_ex_gst > 0 && group.override_cost_ex_gst > 0 && (
          <div className="mt-3 p-2 bg-green-50 rounded text-xs">
            <span className="text-green-700">Margin: {formatCurrency(group.override_sell_ex_gst - group.override_cost_ex_gst)} ({(((group.override_sell_ex_gst - group.override_cost_ex_gst) / group.override_sell_ex_gst) * 100).toFixed(1)}%)</span>
          </div>
        )}
        {!group.override_sell_ex_gst && !group.override_cost_ex_gst && (
          <div className="mt-3 p-2 bg-amber-50 rounded text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            <span className="text-amber-700">No pricing entered yet. This print group won't affect totals until you add a sell or cost price.</span>
          </div>
        )}
      </div>
    );
  }

  return null;
}
